import { TENANT_BASE_DOMAIN } from '@/config';

export function money(amount: number, currency: string | null): string {
  const formatted = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(amount);
  return currency ? `${formatted} ${currency}` : formatted;
}

export function shortDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

export function agencySubdomain(slug: string): string {
  return `${slug}.${TENANT_BASE_DOMAIN || 'localhost'}`;
}

export function publicHost(agency: { slug: string; customDomain: string | null }): string {
  return agency.customDomain || agencySubdomain(agency.slug);
}

export function hostHref(host: string): string {
  const scheme = host.endsWith('.localhost') || host === 'localhost' ? 'http' : 'https';
  return `${scheme}://${host}`;
}

export function storefrontHref(agency: { slug: string; customDomain: string | null }): string {
  return hostHref(publicHost(agency));
}

const ACTION_LABEL: Record<string, string> = {
  'agency.created': 'Created an agency',
  'agency.updated': 'Updated an agency',
  'agency.paused': 'Paused an agency',
  'agency.resumed': 'Resumed an agency',
  'staff.disabled': 'Disabled a staff account',
  'staff.enabled': 'Enabled a staff account',
  'staff.password_reset': 'Reset a staff password',
  'staff.invited': 'Invited a staff member',
  'staff.invite_revoked': 'Revoked a staff invitation',
  'staff.invite_accepted': 'Accepted a staff invitation',
  'profile.updated': 'Updated a profile',
  'impersonation.started': 'Opened an agency as its admin',
  'impersonation.ended': 'Left an impersonation session',
  'tenant.provisioned': 'Provisioned an agency',
  'tenant.paused': 'Paused an agency',
  'tenant.resumed': 'Resumed an agency',
  'tenant.plan_changed': 'Changed a plan',
  'staff.created': 'Added a staff account',
  'staff.updated': 'Updated a staff account',
  'plan.created': 'Added a subscription plan',
  'plan.updated': 'Updated a subscription plan',
  'tenant.migrated': 'Repaired a tenant database',
  'tenant.profile_updated': 'Updated an agency profile',
  'tenant.theme_updated': 'Updated a storefront theme',
  'tenant.theme_document_updated': 'Updated the visual theme',
  'tenant.storefront_updated': 'Updated storefront copy',
  'tenant.site_config_created': 'Created a website page',
  'tenant.site_config_updated': 'Updated a website page',
  'tenant.site_config_published': 'Published a website page',
  'tenant.site_config_reverted': 'Reverted a website page',
  'tenant.site_page_deleted': 'Deleted a website page',
  'tenant.domain_connected': 'Connected a domain',
  'tenant.domain_cleared': 'Removed a custom domain',
  'tenant.backed_up': 'Archived a tenant database',
  'invoice.created': 'Recorded an invoice',
  'invoice.updated': 'Updated an invoice',
  'payment.recorded': 'Recorded a payment',
  'refund.recorded': 'Recorded a refund',
  'platform_user.created': 'Created a platform account',
  'platform_user.updated': 'Updated a platform account',
  'note.created': 'Added an internal note',
  'settings.updated': 'Updated platform settings',
  'settings.cache_cleared': 'Forgot the settings cache',
  'job.retried': 'Retried a failed job',
  settings_cache_cleared: 'Forgot the settings cache',
  job_retried: 'Retried a failed job',
  'billing.exported': 'Exported the billing ledger',
  role_switched: 'Changed a staff role',
  permission_changed: 'Changed feature access',
  impersonation_started: 'Opened an agency as its admin',
  impersonation_ended: 'Left an impersonation session',
  tenant_paused: 'Paused an agency',
  tenant_resumed: 'Resumed an agency',
  tenant_plan_changed: 'Changed a plan',
  tenant_provisioned: 'Provisioned an agency',
  tenant_migrated: 'Repaired a tenant database',
  tenant_profile_updated: 'Updated an agency profile',
  tenant_theme_updated: 'Updated a storefront theme',
  tenant_theme_document_updated: 'Updated the visual theme',
  tenant_storefront_updated: 'Updated storefront copy',
  tenant_domain_connected: 'Connected a domain',
  tenant_domain_cleared: 'Removed a custom domain',
  tenant_backed_up: 'Archived a tenant database',
  staff_created: 'Added a staff account',
  staff_updated: 'Updated a staff account',
  staff_disabled: 'Disabled a staff account',
  staff_enabled: 'Enabled a staff account',
  staff_password_reset: 'Reset a staff password',
  staff_invited: 'Invited a staff member',
  staff_invite_revoked: 'Revoked a staff invitation',
  staff_invite_accepted: 'Accepted a staff invitation',
  profile_updated: 'Updated a profile',
  platform_user_created: 'Created a platform account',
  platform_user_updated: 'Updated a platform account',
  plan_created: 'Added a subscription plan',
  plan_updated: 'Updated a subscription plan',
  two_factor_enrolled: 'Enrolled two-factor authentication',
  two_factor_reset: 'Reset two-factor authentication',
  invoice_created: 'Recorded an invoice',
  invoice_updated: 'Updated an invoice',
  payment_recorded: 'Recorded a payment',
  refund_recorded: 'Recorded a refund',
  note_created: 'Added an internal note',
  settings_updated: 'Updated platform settings',
  settings_secret_replaced: 'Replaced a platform credential',
  settings_secret_removed: 'Removed a platform credential',
  integration_tested: 'Tested a platform integration',
  billing_exported: 'Exported the billing ledger',
};

export function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

export function slugFromName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}
